import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Star, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { ContractDialog } from "@/components/ContractDialog";

export default function PricingPage() {
  const [billing, setBilling] = useState(() => localStorage.getItem("licitaai_billing") || "monthly");
  const [plans, setPlans] = useState([]);
  const [contractOpen, setContractOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("licitaai_billing", billing);
  }, [billing]);

  const handleContract = (plan) => {
    setSelectedPlan(plan);
    setContractOpen(true);
  };

  const fmt = (n) => `Q${(n || 0).toLocaleString()}`;

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <header className="border-b border-border/40 bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading font-black text-xl tracking-tight">
            LICITA<span className="text-[#002FA7]">AI</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(user ? "/" : "/login")} className="rounded-none" data-testid="back-btn">
              <ArrowLeft size={16} className="mr-2" />
              {user ? "Volver al panel" : "Iniciar sesión"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">
            Planes y precios
          </h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto">
            Inteligencia en licitaciones para empresas de todos los tamaños. Cancela cuando quieras.
          </p>

          {/* Billing toggle */}
          <div className="mt-10 inline-flex items-center bg-secondary p-1 border border-border" data-testid="billing-toggle">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-6 py-2 text-sm font-mono uppercase tracking-wide transition-colors ${
                billing === "monthly" ? "bg-[#002FA7] text-white" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="billing-monthly"
            >
              Mensual
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`px-6 py-2 text-sm font-mono uppercase tracking-wide transition-colors ${
                billing === "annual" ? "bg-[#002FA7] text-white" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="billing-annual"
            >
              Anual
              <span className="ml-2 text-[10px] bg-[#2A9D8F] text-white px-2 py-0.5">2 MESES GRATIS</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {plans.map((p) => {
            const price = billing === "annual" ? p.annual_equivalent_monthly : p.price_monthly;
            const annualTotal = p.price_annual;
            return (
              <Card
                key={p.id}
                className={`rounded-none p-6 flex flex-col relative ${
                  p.popular ? "border-2 border-[#002FA7] shadow-lg" : "border border-border"
                }`}
                data-testid={`plan-card-${p.id}`}
              >
                {p.popular && (
                  <div className="absolute -top-3 left-6 bg-[#002FA7] text-white px-3 py-1 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1">
                    <Star size={10} weight="fill" /> Más popular
                  </div>
                )}
                <h3 className="font-heading font-black text-2xl tracking-tight">{p.name}</h3>

                <div className="mt-4 mb-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black">{fmt(price)}</span>
                    <span className="text-sm text-muted-foreground">/mes</span>
                  </div>
                  {billing === "annual" && (
                    <div className="mt-2 text-[11px] font-mono text-muted-foreground">
                      Total anual: <strong>{fmt(annualTotal)}</strong>
                    </div>
                  )}
                  {billing === "annual" && (
                    <div className="mt-1 inline-block bg-[#2A9D8F]/10 text-[#2A9D8F] px-2 py-0.5 text-[10px] font-mono font-bold">
                      Ahorra {fmt(p.annual_savings)}
                    </div>
                  )}
                </div>

                <ul className="mt-6 mb-6 space-y-2.5 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check size={16} weight="bold" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleContract(p)}
                  className={`rounded-none w-full ${
                    p.popular
                      ? "bg-[#002FA7] hover:bg-[#001D66] text-white"
                      : "bg-foreground hover:bg-foreground/90 text-background"
                  }`}
                  data-testid={`contract-btn-${p.id}`}
                >
                  Contratar
                </Button>
              </Card>
            );
          })}
        </div>

        <p className="text-center mt-12 text-xs font-mono text-muted-foreground">
          ¿Tienes preguntas? Escríbenos a{" "}
          <a href="mailto:sales@aurora-antartic.com" className="text-[#002FA7] hover:underline">
            sales@aurora-antartic.com
          </a>
        </p>
      </main>

      <ContractDialog
        open={contractOpen}
        onOpenChange={setContractOpen}
        plan={selectedPlan}
        billing={billing}
      />
    </div>
  );
}
